import {defineToolRegistryManifest} from '../../types/base-tool-types';
import {z} from 'zod';
import dedent from 'dedent';
import {elementIdSchema} from '../../schemas';

const toolName = 'set-form-input-type' as const;

const inputTypeSchema = z
  .union([
    z.literal('text'),
    z.literal('email'),
    z.literal('password'),
    z.literal('tel'),
    z.literal('number'),
    z.literal('url'),
  ])
  .describe(
    'The HTML input type to set: text (plain text), email (email validation), password (hidden text), tel (phone number), number (numeric input), or url (URL validation).'
  );

const argumentZod = z.object({
  elementId: elementIdSchema.describe(
    'The ID of the FormTextInput element to set the input type on. Only works with FormTextInput elements.'
  ),
  inputType: inputTypeSchema,
});

export const setFormInputTypeTool = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Set form input type',
  prettyErrors: {
    fallback: 'Unable to set form input type.',
    patterns: [
      {
        match: 'not found',
        message: 'Element not found.',
      },
      {
        match: 'not a form input',
        message: 'This element is not a form input.',
      },
    ],
  },
  llmTool: {
    name: toolName,
    description: dedent`
      Set the HTML input type of a FormTextInput element. Changes how the browser
      handles the input field including validation, keyboard layout, and formatting.
      Available types: text (default), email (email validation), password (hidden
      input), tel (telephone), number (numeric input), url (URL validation). Only
      works with FormTextInput elements. Returns null when the input type is
      successfully set. Input types improve user experience and data validation.`,
    inputSchema: argumentZod,
  },
  type: 'designer',
  confirmBeforeExecuting: true, // Modifies input behavior and validation, requires confirmation
  timeout: 30000, // 30 seconds for setting input type
});
